import React, { useEffect, useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { usersApi } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface UserPreferences {
  preferredPageSize: number;
  preferredTheme: string;
  emailNotificationsEnabled: boolean;
}

const UserSettings: React.FC = () => {
  const { refreshPreferences } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [theme, setTheme] = useState('light');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const data = await usersApi.getPreferences();
        setPreferences(data);
        setPageSize(data.preferredPageSize);
        setTheme(data.preferredTheme);
        setEmailNotifications(data.emailNotificationsEnabled);
      } catch (error) {
        console.error('Failed to fetch preferences:', error);
        setErrorMessage('Failed to load preferences');
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, []);

  const handleSavePreferences = async () => {
    if (pageSize < 3 || pageSize > 100) {
      setErrorMessage('Page size must be between 3 and 100');
      return;
    }

    setSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await usersApi.updatePreferences({
        preferredPageSize: pageSize,
        preferredTheme: theme,
        emailNotificationsEnabled: emailNotifications,
      });
      setSuccessMessage('Preferences saved successfully');
      setPreferences({
        preferredPageSize: pageSize,
        preferredTheme: theme,
        emailNotificationsEnabled: emailNotifications,
      });
      // Refresh preferences in global context so other components can react to changes
      await refreshPreferences();
    } catch (error) {
      console.error('Failed to save preferences:', error);
      setErrorMessage('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (preferences) {
      setPageSize(preferences.preferredPageSize);
      setTheme(preferences.preferredTheme);
      setEmailNotifications(preferences.emailNotificationsEnabled);
      setSuccessMessage('');
      setErrorMessage('');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!preferences) {
    return (
      <Container maxWidth="md">
        <Alert severity="error">Failed to load preferences</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Settings
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Manage your application preferences
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Display Settings
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Theme</InputLabel>
              <Select
                value={theme}
                label="Theme"
                onChange={(e) => setTheme(e.target.value)}
                disabled={saving}
              >
                <MenuItem value="light">Light</MenuItem>
                <MenuItem value="dark">Dark</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Items Per Page"
              type="number"
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value))}
              fullWidth
              variant="outlined"
              disabled={saving}
              inputProps={{ min: 3, max: 100 }}
              helperText="Number of items to display per page (3-100)"
            />
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Notification Settings
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <FormControlLabel
            control={
              <Switch
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
                disabled={saving}
              />
            }
            label="Enable Email Notifications"
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 1 }}>
            Receive email notifications for important account events and invitations
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleSavePreferences}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={saving}
            >
              Reset
            </Button>
            <Button
              variant="text"
              onClick={() => navigate('/profile')}
              disabled={saving}
            >
              Back to Profile
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default UserSettings;
